import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Building2 } from 'lucide-react';
import axiosInstance from '../api/axiosInstance';
import API_ENDPOINTS from '../api/endpoints';
import { isSuperAdmin } from '../utils/roles';
import styles from './SpoofUI.module.css';

const KEY = 'orgScope';
export const readOrgScope = () => { try { return localStorage.getItem(KEY) || ''; } catch (e) { return ''; } };

/**
 * Superadmin only: which organisation the Leads, Users, Queues… screens show.
 * The choice is sent as `x-org-scope` on every request (axiosInstance) and the
 * backend re-targets the session to that organisation. Changing it reloads,
 * so every cached list is refetched for the new tenant.
 */
export default function OrgScopeSwitcher() {
  const { user } = useSelector((state) => state.auth);
  const [orgs, setOrgs] = useState([]);
  const [scope, setScope] = useState(readOrgScope());
  const superadmin = isSuperAdmin(user);

  useEffect(() => {
    if (!superadmin) return undefined;
    let cancelled = false;
    axiosInstance.get(API_ENDPOINTS.superadmin.organisations, { params: { page: 1, limit: 100 } })
      .then((res) => { if (!cancelled) setOrgs(res.data?.response?.[0]?.data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [superadmin]);

  if (!superadmin) return null;
  const ownOrgId = String(user?.userOrgId || '');
  const change = (e) => {
    const next = e.target.value;
    try {
      if (!next || next === ownOrgId) localStorage.removeItem(KEY); else localStorage.setItem(KEY, next);
    } catch (err) { /* storage unavailable */ }
    setScope(next === ownOrgId ? '' : next);
    window.location.reload();
  };
  const current = orgs.find((o) => String(o._id) === (scope || ownOrgId));
  return (
    <div className={styles.spoofTriggerWrap} title="Organisation whose leads, users and queues you are viewing">
      <label className={styles.spoofTrigger} style={{ cursor: 'default', gap: 8 }}>
        <Building2 />
        <select
          value={scope || ownOrgId}
          onChange={change}
          aria-label="Organisation scope"
          style={{ border: 'none', background: 'transparent', font: 'inherit', color: scope ? 'var(--color-primary)' : 'inherit', fontWeight: scope ? 600 : 500, cursor: 'pointer', outline: 'none' }}
        >
          {orgs.length === 0 && <option value={scope || ownOrgId}>{current?.orgName || 'Organisation'}</option>}
          {orgs.map((o) => (
            <option key={o._id} value={String(o._id)}>{o.orgName}{String(o._id) === ownOrgId ? ' (mine)' : ''}{o.orgActive === false ? ' — off' : ''}</option>
          ))}
        </select>
      </label>
    </div>
  );
}
